import PropTypes from "prop-types";
import React, { Fragment } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import filterFactory, { textFilter } from "react-bootstrap-table2-filter";
import paginator from "react-bootstrap-table2-paginator";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router";

// App
import { ButtonDeleteConfirm, ButtonEdit } from "../../core/components";
import { ROUTE_ORGANIZATION_EDIT } from "../../../constants";

// Auth
import { usePermission } from "../../auth/hooks/usePermission";

const OrganizationGrid = (props) => {
  const { gridData, gridDataTotal, gridIsDeleting, gridPage, gridPageSize, gridSortField, gridSortOrder } = props;
  const [hasPermission] = usePermission();
  const { t } = useTranslation();

  async function handleChange(type, { filters, page, sizePerPage, sortField, sortOrder }) {
    const { gridChangeFilter, gridChangePagination, gridChangeSort, gridFetch } = props;

    switch (type) {
      case "filter":
        await gridChangeFilter(filters);
        break;
      case "pagination":
        await gridChangePagination(page, sizePerPage);
        break;
      case "sort":
        await gridChangeSort(sortField, sortOrder);
        break;
      default:
        throw Error("Unknow type");
    }

    await gridFetch();
  }

  function handleDelete(id) {
    props.gridDelete(id).then(() => {
      props.gridFetch();
    });
  }

  // Columns
  const columns = [
    {
      dataField: "nameComposed",
      filter: textFilter(),
      sort: true,
      text: t("title.name"),
    },
    {
      align: "right",
      classes: "react-bootstrap-table-buttons",
      dataField: "id",
      filterable: false,
      isDummyField: true,
      formatter: (cellContent, row) => {
        return (
          <Fragment>
            {hasPermission("organization.update") && (
              <ButtonEdit to={generatePath(ROUTE_ORGANIZATION_EDIT, { id: row.id })} />
            )}
            {hasPermission("organization.delete") && (
              <ButtonDeleteConfirm
                buttonNoDisabled={gridIsDeleting}
                buttonYesDisabled={gridIsDeleting}
                buttonYesExecuting={gridIsDeleting}
                buttonYesOnClick={() => handleDelete(row.id)}
                modalBody={t("text.doYouReallyWantToDeleteThisItem")}
                title={t("action.delete")}
              />
            )}
          </Fragment>
        );
      },
      text: "",
    },
  ];

  return (
    <BootstrapTable
      bootstrap4
      bordered={false}
      columns={columns}
      data={gridData}
      defaultSorted={[
        {
          dataField: gridSortField,
          order: gridSortOrder,
        },
      ]}
      filter={filterFactory()}
      keyField="id"
      onTableChange={handleChange}
      pagination={paginator({
        firstPageTitle: t("action.goToFirst"),
        lastPageTitle: t("action.goToLast"),
        nextPageTitle: t("action.goToNext"),
        prePageTitle: t("action.goToPrevious"),
        page: gridPage,
        showTotal: false,
        sizePerPage: gridPageSize,
        totalSize: gridDataTotal,
      })}
      remote={{
        filter: true,
        pagination: true,
        sort: true,
      }}
    />
  );
};

OrganizationGrid.propTypes = {
  gridChangeFilter: PropTypes.func.isRequired,
  gridChangePagination: PropTypes.func.isRequired,
  gridChangeSort: PropTypes.func.isRequired,
  gridData: PropTypes.array.isRequired,
  gridDataTotal: PropTypes.number.isRequired,
  gridDelete: PropTypes.func.isRequired,
  gridFetch: PropTypes.func.isRequired,
  gridIsDeleting: PropTypes.bool.isRequired,
  gridPage: PropTypes.number.isRequired,
  gridPageSize: PropTypes.number.isRequired,
  gridSortField: PropTypes.string.isRequired,
  gridSortOrder: PropTypes.string.isRequired,
};

export default OrganizationGrid;
